import { useCallback } from 'react';
import { useSwrMissingInfo } from '@hooks/useSwrApi/endpoints/common/useSwrMissingInfo';
import { useSwrCompanyOnboarding } from '@hooks/useSwrApi/endpoints/company/useSwrCompanyOnboarding';
import { useSwrCreateCompany } from '@hooks/useSwrApi/endpoints/company/useSwrCreateCompany';
import { showPhoneFlowModal, closePhoneFlowModal } from '@components/features/modals/phone/PhoneFlowModal';
import { showAddBillingDataModal } from '@components/features/modals/billing-data/AddBillingDataModal';

/** Props */
interface UseOfferPageServicesProps {
  locale: Locale;
  isAuth: boolean;
}

/**
 * @description Hook for managing business data and phone validation flow for offer page services.
 * Handles the validation and modal flows before activating Bestie screening packages.
 * - `locale` - The current app locale.
 * - `isAuth` - Whether the user is authenticated.
 */
export const useOfferPageServices = (props: UseOfferPageServicesProps) => {
  const { locale, isAuth } = props;

  // Fetch missing info (business data, phone, phone confirmation)
  const {
    data: missingInfo,
    mutate: getMissingInfo,
  } = useSwrMissingInfo({
    locale,
    shouldFetch: isAuth,
    type: 'business',
  });

  // Fetch employer data (only when authenticated)
  const { data: employerData } = useSwrCompanyOnboarding(locale, isAuth);

  // Create company hook
  const { trigger: createCompany } = useSwrCreateCompany({
    locale,
    successCallback: () => {
      if (missingInfo?.fields?.includes('phoneConfirm')) {
        // Complete the phone confirmation if the phone is not confirmed
        showPhoneFlowModal({
          locale,
          startFromConfirm: true,
          successConfirmPhoneCallback: () => {
            void getMissingInfo();
            closePhoneFlowModal();
          },
        });
      }
    },
  });

  /**
   * @description Validates business data and phone confirmation before executing the activation callback.
   * Shows appropriate modals for missing information.
   * - `activationCallback` - The callback to execute after validation passes.
   */
  const validateAndActivate = useCallback((activationCallback: () => void | Promise<void>) => {
    // Check if there's missing info
    if (missingInfo && missingInfo?.fields && missingInfo.fields.length > 0) {
      // Handle missing business data / profile
      if (missingInfo.fields.includes('profile') && employerData) {
        // If no company name but has individual data, create an individual company
        if (employerData?.firstName && employerData?.lastName && employerData?.phone?.number) {
          void createCompany({
            ...employerData,
            isIndividual: 1,
          });
        } else {
          // Show billing data modal
          showAddBillingDataModal(locale, employerData, () => {
            void getMissingInfo();
          }, false);
        }
        return;
      }

      // Handle missing phone number
      if (missingInfo.fields.includes('phone')) {
        showPhoneFlowModal({
          locale,
          successConfirmPhoneCallback: () => {
            void getMissingInfo();
            closePhoneFlowModal();
          },
        });
        return;
      }

      // Handle missing phone confirmation
      if (missingInfo.fields.includes('phoneConfirm')) {
        showPhoneFlowModal({
          locale,
          startFromConfirm: true,
          successConfirmPhoneCallback: () => {
            void getMissingInfo();
            closePhoneFlowModal();
          },
        });
        return;
      }
    }

    // All validations passed, execute the activation callback
    void activationCallback();
  }, [locale, getMissingInfo, missingInfo, employerData, createCompany]);

  return {
    validateAndActivate,
    missingInfo,
  };
};
